import React, { useState, useRef } from "react"
import { createPortal } from "react-dom"
import { motion } from "framer-motion"
import {
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  User,
  Plane,
  Globe,
  Building2,
  UserCircle,
} from "lucide-react"
import {
  MOTION_VARIANTS,
  CHAT_TABS,
  TAB_ORDER,
  TAB_LABELS,
  TAB_CAPABILITIES,
  TAB_FILTER_FIELDS,
} from "./constants"
import FilterPanel, { FilterTrigger, countActiveFilters } from "./FilterPanel"
import { formatTelegramDate } from "../../utils/date/format"
import { useTranslation } from "react-i18next"
import { extractMainMessage } from "../../utils/serializers/chat"

const GlobalTooltip = ({ tooltip }) => {
  if (!tooltip) return null
  return createPortal(
    <div
      className="fixed z-[9999] w-72 max-w-xs pointer-events-none"
      style={{ top: tooltip.top, left: tooltip.left, transform: "translateY(-50%)" }}
    >
      <div className="flex items-center">
        <div className="w-2 h-2 bg-slate-800 rotate-45 flex-shrink-0 -mr-1" />
        <div
          className="bg-slate-800 text-white text-xs rounded-lg px-3 py-2 shadow-lg break-words leading-relaxed"
          dangerouslySetInnerHTML={{ __html: tooltip.message }}
        />
      </div>
    </div>,
    document.body
  )
}

const getDepartmentName = (departments, id) => {
  if (!departments || !id) return null
  const dep = departments.find((d) => d.id === id)
  return dep ? dep.name : null
}

const daysColor = (days) => {
  if (days == null) return "bg-slate-100 text-slate-500"
  if (days <= 3) return "bg-red-100 text-red-600"
  if (days <= 7) return "bg-orange-100 text-orange-600"
  return "bg-emerald-100 text-emerald-700"
}

// ---------------------------------------------------------------------------
// Skeleton — заглушка при первой загрузке
// ---------------------------------------------------------------------------
const ChatItemSkeleton = () => (
  <div className="flex items-start gap-3 px-3 py-3 border-b border-slate-100 animate-pulse">
    <div className="flex-1 min-w-0 space-y-2">
      <div className="flex items-center gap-1.5">
        <div className="h-5 w-12 rounded bg-slate-200" />
        <div className="h-5 w-20 rounded bg-slate-200" />
        <div className="h-5 w-16 rounded bg-slate-200 ml-auto" />
      </div>
      <div className="h-3.5 w-3/4 rounded bg-slate-100" />
    </div>
  </div>
)

const ChatListSkeleton = () => (
  <>
    {Array.from({ length: 7 }).map((_, i) => (
      <ChatItemSkeleton key={i} />
    ))}
  </>
)

// ---------------------------------------------------------------------------
// ChatItem для табов bundle (ALL, FLIGHT_CHANGES)
// ---------------------------------------------------------------------------
const BundleChatItem = ({
  chat,
  index,
  selectedChat,
  onSelectChat,
  t,
  onShowTooltip,
  onHideTooltip,
  departments,
  activeTab,
}) => {
  const msgRef = useRef(null)
  const departmentName = getDepartmentName(departments, chat.departament_id)
  const showDeptBadge = TAB_CAPABILITIES[activeTab]?.canFilterByDepartment

  const handleMouseEnter = () => {
    if (!chat.message || !msgRef.current) return
    const rect = msgRef.current.getBoundingClientRect()
    onShowTooltip({ message: chat.message, top: rect.top + rect.height / 2, left: rect.right + 8 })
  }

  return (
    <motion.div
      custom={index}
      variants={MOTION_VARIANTS.item}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      onClick={() => onSelectChat(chat)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={onHideTooltip}
      className={`flex items-start gap-3 px-3 py-3 cursor-pointer transition-colors hover:bg-slate-50 border-b border-slate-100 last:border-0 ${
        selectedChat?.bundle_id === chat.bundle_id ? "bg-blue-50 hover:bg-blue-50" : ""
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="flex flex-wrap gap-1 flex-1 min-w-0">
            <button className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors">
              #{chat.bundle_id}
            </button>
            {chat.user_name && (
              <button className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors">
                <User className="w-3 h-3 flex-shrink-0" />
                <span className="max-w-[90px] truncate">{chat.user_name}</span>
              </button>
            )}
            {chat.days_to_flight != null && (
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${daysColor(chat.days_to_flight)}`}
              >
                <Plane className="w-3 h-3 flex-shrink-0" />
                {chat.days_to_flight}д
              </span>
            )}
            {chat.state_name && (
              <button className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors">
                <Globe className="w-3 h-3 flex-shrink-0" />
                <span className="max-w-[80px] truncate">{chat.state_name}</span>
              </button>
            )}
            {showDeptBadge && departmentName && (
              <button className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-violet-50 text-violet-600 hover:bg-violet-100 transition-colors">
                <Building2 className="w-3 h-3 flex-shrink-0" />
                <span className="max-w-[80px] truncate">{departmentName}</span>
              </button>
            )}
          </div>
          <span className="text-xs text-slate-400 flex-shrink-0 mt-0.5">
            {formatTelegramDate(chat.created_at)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <p
            ref={msgRef}
            className="text-xs text-slate-400 line-clamp-1 flex-1"
            dangerouslySetInnerHTML={{ __html: extractMainMessage(chat.message) }}
          />
          {chat.unread_count > 0 && (
            <div className="ml-2 min-w-[20px] h-5 px-1.5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-medium">{chat.unread_count}</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  )
}

// ---------------------------------------------------------------------------
// ChatItem для таба LEADS
// ---------------------------------------------------------------------------
const LeadChatItem = ({
  chat,
  index,
  selectedChat,
  onSelectChat,
  t,
  onShowTooltip,
  onHideTooltip,
}) => {
  const msgRef = useRef(null)

  const handleMouseEnter = () => {
    if (!chat.message || !msgRef.current) return
    const rect = msgRef.current.getBoundingClientRect()
    onShowTooltip({ message: chat.message, top: rect.top + rect.height / 2, left: rect.right + 8 })
  }

  return (
    <motion.div
      custom={index}
      variants={MOTION_VARIANTS.item}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      onClick={() => onSelectChat(chat)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={onHideTooltip}
      className={`flex items-start gap-3 px-3 py-3 cursor-pointer transition-colors hover:bg-slate-50 border-b border-slate-100 last:border-0 ${
        selectedChat?.bundle_id === chat.bundle_id ? "bg-blue-50 hover:bg-blue-50" : ""
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="flex flex-wrap gap-1 flex-1 min-w-0">
            <button className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors">
              #{chat.bundle_id}
            </button>

            {chat.lead_name && (
              <button className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors">
                <UserCircle className="w-3 h-3 flex-shrink-0" />
                <span className="max-w-[100px] truncate">{chat.lead_name}</span>
              </button>
            )}

            {chat.state_name && (
              <button className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors">
                <Globe className="w-3 h-3 flex-shrink-0" />
                <span className="max-w-[80px] truncate">{chat.state_name}</span>
              </button>
            )}

            {chat.manager_name && (
              <button className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors">
                <User className="w-3 h-3 flex-shrink-0" />
                <span className="max-w-[80px] truncate">{chat.manager_name}</span>
              </button>
            )}

            {chat.lead_status_name && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
                style={{
                  backgroundColor: chat.lead_status_bg || "#f1f5f9",
                  color: chat.lead_status_color || "#475569",
                }}
              >
                {chat.lead_status_name}
              </span>
            )}

            {chat.days_to_flight != null && (
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${daysColor(chat.days_to_flight)}`}
              >
                <Plane className="w-3 h-3 flex-shrink-0" />
                {chat.days_to_flight}д
              </span>
            )}
          </div>
          <span className="text-xs text-slate-400 flex-shrink-0 mt-0.5">
            {formatTelegramDate(chat.created_at)}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <p
            ref={msgRef}
            className="text-xs text-slate-400 line-clamp-1 flex-1"
            dangerouslySetInnerHTML={{ __html: chat.message }}
          />
          {chat.unread_count > 0 && (
            <div className="ml-2 min-w-[20px] h-5 px-1.5 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-medium">{chat.unread_count}</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  )
}

// ---------------------------------------------------------------------------
// Единая точка входа — выбирает нужный компонент по табу
// ---------------------------------------------------------------------------
const ChatItem = ({ activeTab, ...props }) => {
  if (activeTab === CHAT_TABS.LEADS) {
    return <LeadChatItem {...props} activeTab={activeTab} />
  }
  return <BundleChatItem {...props} activeTab={activeTab} />
}

// ---------------------------------------------------------------------------
// Chips
// ---------------------------------------------------------------------------
const Chip = ({ label, onRemove }) => (
  <div className="flex items-center gap-1 px-2 py-0.5 bg-orange-50 border border-orange-200 rounded-full">
    <span className="text-xs text-orange-700 max-w-[80px] truncate">{label}</span>
    <button onClick={onRemove} className="flex-shrink-0 hover:text-red-500 transition-colors">
      <X className="w-3 h-3 text-orange-400" />
    </button>
  </div>
)

const ActiveChips = ({ filters, activeTab, onFilterChange, onFilterReset, departments, t }) => {
  const fields = TAB_FILTER_FIELDS[activeTab] ?? []
  const chips = []

  if (fields.includes("status") && filters.status !== "")
    chips.push(
      <Chip key="status" label={filters.status} onRemove={() => onFilterChange("status", "")} />
    )

  if (fields.includes("state_id") && filters.state_id > 0)
    chips.push(
      <Chip
        key="state_id"
        label={`${t("admin.state")}: ${filters.state_id}`}
        onRemove={() => onFilterChange("state_id", 0)}
      />
    )

  if (fields.includes("departament_id") && filters.departament_ids?.length > 0)
    chips.push(
      <Chip
        key="departament_ids"
        label={`${t("admin.departament")}: ${filters.departament_ids.length}`}
        onRemove={() => onFilterChange("departament_ids", [])}
      />
    )

  if (fields.includes("company_id") && filters.company_id > 0)
    chips.push(
      <Chip
        key="company_id"
        label={`${t("devise.company")}: ${filters.company_id}`}
        onRemove={() => onFilterChange("company_id", 0)}
      />
    )

  if (fields.includes("moderator_id") && filters.moderator_id > 0)
    chips.push(
      <Chip
        key="moderator_id"
        label={`${t("admin.moderator")}: ${filters.moderator_id}`}
        onRemove={() => onFilterChange("moderator_id", 0)}
      />
    )

  if (fields.includes("bundle_type") && filters.bundle_type)
    chips.push(
      <Chip
        key="bundle_type"
        label={`${t("admin.orders_type")}: ${filters.bundle_type}`}
        onRemove={() => onFilterChange("bundle_type", "")}
      />
    )

  if (fields.includes("message_theme") && filters.message_theme?.length > 0)
    chips.push(
      <Chip
        key="message_theme"
        label={`${t("admin.topics")}: ${filters.message_theme.length}`}
        onRemove={() => onFilterChange("message_theme", [])}
      />
    )

  if (fields.includes("date_range") && (filters.date_from || filters.date_to))
    chips.push(
      <Chip
        key="date_range"
        label={`${filters.date_from || "…"} — ${filters.date_to || "…"}`}
        onRemove={() => {
          onFilterChange("date_from", null)
          onFilterChange("date_to", null)
        }}
      />
    )

  if (fields.includes("search") && filters.search)
    chips.push(
      <Chip
        key="search"
        label={`"${filters.search}"`}
        onRemove={() => onFilterChange("search", "")}
      />
    )

  if (chips.length === 0) return null

  return (
    <div className="px-3 py-2 border-b border-slate-100 flex flex-wrap gap-1.5 flex-shrink-0">
      {chips}
      <button
        onClick={onFilterReset}
        className="text-xs text-slate-400 hover:text-red-500 transition-colors px-1"
      >
        {t("admin.reset_all")}
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ChatListSidebar
// ---------------------------------------------------------------------------
const ChatListSidebar = ({
  sidebarRef,
  isMobileView,
  selectedChat,
  isMobile,
  sidebarWidth,
  filteredChats,
  filters,
  onFilterChange,
  onFilterReset,
  onFilterSearch,
  activeTab,
  setActiveTab,
  onSelectChat,
  onClose,
  onStartResize,
  isAdmin,
  departments,
  sentinelRef,
  isLoadingMore,
  hasMore,
  onCollapse,
  onExpand,
  totalUnread,
  leadsUnread,
  messagethemes,
}) => {
  const { t } = useTranslation()
  const [showFilters, setShowFilters] = useState(false)
  const [tooltip, setTooltip] = useState(null)

  const activeCount = countActiveFilters(filters, activeTab)
  const isCollapsed = sidebarWidth === 0

  const isLeadsTab = activeTab === CHAT_TABS.LEADS
  const searchValue = isLeadsTab ? filters.lead_id || "" : filters.bundle_id || ""
  const searchField = isLeadsTab ? "lead_id" : "bundle_id"
  const searchPlaceholder = isLeadsTab
    ? t("admin.search_lead_id", "Lead ID")
    : t("admin.search_bundle_id", "Search")

  // Первая загрузка — список пуст и запрос ещё идёт
  const isInitialLoading = isLoadingMore && filteredChats.length === 0

  if (isCollapsed) {
    return (
      <div className="relative w-0 flex-shrink-0">
        <button
          onClick={onExpand}
          className="absolute top-1/2 -translate-y-1/2 left-0 z-20 flex items-center justify-center w-5 h-10 bg-white border border-slate-200 border-l-0 rounded-r-lg shadow-md hover:bg-blue-50 hover:border-blue-300 transition-colors group"
          title={t("admin.show_chat_list", "Показать список чатов")}
        >
          <ChevronRight className="w-3 h-3 text-slate-400 group-hover:text-blue-500 transition-colors" />
        </button>
      </div>
    )
  }

  return (
    <div
      className={`${isMobileView && selectedChat ? "hidden" : "flex"} relative flex-shrink-0`}
      style={{
        width: isMobile ? "100%" : `${sidebarWidth}%`,
        minWidth: isMobile ? "100%" : "200px",
        maxWidth: isMobile ? "100%" : "600px",
      }}
    >
      {!isMobile && onCollapse && (
        <button
          onClick={onCollapse}
          className="absolute top-1/2 -translate-y-1/2 -right-[10px] flex items-center justify-center w-5 h-10 bg-white border border-slate-200 border-l-0 rounded-r-lg shadow-md hover:bg-blue-50 hover:border-blue-300 transition-colors z-40"
          title={t("admin.hide_chat_list")}
        >
          <ChevronLeft className="w-3 h-3 text-slate-400 hover:text-blue-500 transition-colors" />
        </button>
      )}

      <motion.div
        ref={sidebarRef}
        className="flex flex-col bg-white border-r border-slate-200 relative w-full overflow-hidden"
        variants={MOTION_VARIANTS.sidebar}
        initial="hidden"
        animate="visible"
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-3 border-b border-slate-200 flex-shrink-0">
          <FilterTrigger onClick={() => setShowFilters(true)} activeCount={activeCount} />
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              type="text"
              inputMode="numeric"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => {
                const value = e.target.value
                if (/^\d*$/.test(value)) onFilterChange(searchField, value)
              }}
              className="w-full pl-9 pr-3 py-1.5 bg-slate-100 rounded-full text-sm focus:outline-none focus:bg-white focus:ring-1 focus:ring-blue-500 transition-all"
            />
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-red-50 text-slate-500 hover:text-red-500 rounded-full transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <ActiveChips
          filters={filters}
          activeTab={activeTab}
          onFilterChange={onFilterChange}
          onFilterReset={onFilterReset}
          departments={departments}
          t={t}
        />

        {/* Tabs */}
        <div className="flex border-b border-slate-100 flex-shrink-0 overflow-x-auto scrollbar-none">
          {TAB_ORDER.map((tabKey) => {
            const isActive = activeTab === tabKey
            const badge =
              tabKey === CHAT_TABS.ALL && totalUnread > 0
                ? totalUnread
                : tabKey === CHAT_TABS.LEADS && leadsUnread > 0
                  ? leadsUnread
                  : null
            return (
              <button
                key={tabKey}
                onClick={() => setActiveTab(tabKey)}
                className={`flex-shrink-0 px-4 py-2.5 text-sm font-medium transition-colors relative whitespace-nowrap ${
                  isActive
                    ? "text-blue-500 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-blue-500"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <span className="flex items-center justify-center gap-1.5">
                  {t(TAB_LABELS[tabKey], tabKey)}
                  {badge && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-blue-500 text-white text-[11px] font-semibold leading-none">
                      {badge > 99 ? "99+" : badge}
                    </span>
                  )}
                </span>
              </button>
            )
          })}
        </div>

        {/* Chat list */}
        <div className="flex-1 overflow-y-auto">
          {isInitialLoading ? (
            // Первая загрузка — показываем скелетон вместо "ничего не найдено"
            <ChatListSkeleton />
          ) : filteredChats.length === 0 ? (
            <div className="flex items-center justify-center h-24 text-sm text-slate-400">
              {t("admin.nothing_found")}
            </div>
          ) : (
            <>
              {filteredChats.map((chat, i) => (
                <ChatItem
                  key={`${chat.id}-${chat.bundle_id}`}
                  chat={chat}
                  index={i}
                  selectedChat={selectedChat}
                  onSelectChat={onSelectChat}
                  t={t}
                  onShowTooltip={setTooltip}
                  onHideTooltip={() => setTooltip(null)}
                  departments={departments}
                  activeTab={activeTab}
                />
              ))}
              <GlobalTooltip tooltip={tooltip} />

              {hasMore && (
                <div ref={sentinelRef} className="flex justify-center py-4">
                  {isLoadingMore && (
                    <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                  )}
                </div>
              )}
              {!hasMore && filteredChats.length > 0 && (
                <div className="text-center text-xs text-slate-400 py-3">
                  {t("admin.all_chats_are_loaded")}
                </div>
              )}
            </>
          )}
        </div>

        {!isMobile && (
          <div
            className="absolute top-0 right-0 bottom-0 cursor-col-resize group z-30"
            style={{ width: "12px", marginRight: "-6px" }}
            onMouseDown={onStartResize}
          >
            <div className="absolute top-1/2 right-0 -translate-y-1/2 w-1 h-16 bg-slate-300 group-hover:bg-blue-500 rounded-l transition-colors" />
          </div>
        )}

        <FilterPanel
          isOpen={showFilters}
          onClose={() => setShowFilters(false)}
          filters={filters}
          onChange={onFilterChange}
          activeTab={activeTab}
          onSearch={() => {
            onFilterSearch?.()
            setShowFilters(false)
          }}
          onReset={onFilterReset}
          departments={departments}
          messagethemes={messagethemes}
          isAdmin={isAdmin}
        />
      </motion.div>
    </div>
  )
}

export default ChatListSidebar
